import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Gap,
  Grid,
  FormGroup,
  SelectMulti,
  ISelectMultiOption,
  Tooltip,
} from '@salecycle/ui-component-library';
import { FC, useState } from 'react';
import { IBlocklistAllowlistItem } from '../entities/blocklist-allowlist-constants';
import { useParams } from 'react-router-dom';
import BlocklistAllowlistCommentModal from './BlocklistAllowlistCommentModal';

interface IFormProps {
  addBlocklistAllowlistCallback: (newItems: IBlocklistAllowlistItem[]) => void;
  addItemValueToSelectMulti: (inputValue: string) => void;
  itemName: string;
  itemNameChangeHandler: (value: string) => void;
  itemValue: ISelectMultiOption<string>[];
  invalidItemValues: Map<string, string[]>;
  duplicateItemValues: string[];
  setInvalidItemValues: (value: Map<string, string[]>) => void;
  setDuplicateItemValues: (value: string[]) => void;
  itemValueChangeHandler: (value: ISelectMultiOption<string>[]) => void;
  listType: string;
  valueType: string;
  setItemName: (value: string) => void;
  setItemValue: (value: ISelectMultiOption<string>[]) => void;
}

export type Props = IFormProps;

const BlocklistAllowlistForm: FC<IFormProps> = props => {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const save = async () => {
    setIsModalOpen(true);
  };

  const resetItem = () => {
    props.setItemName('');
    props.setItemValue([]);
  };

  const getInvalidWarningDetails = () => {
    let returnString = '';

    // Build up a list of any item that is considered invalid
    const invalidItems = new Set<string>();

    // Foreach type of invalid item
    props.invalidItemValues.forEach((_value, key) => {
      // Add each item to the total list of invalid items (if not already in it)
      _value.forEach((value, i) => {
        invalidItems.add(value);
      });

      // If this isn't some generic error message, stack the warning messages
      if (key !== 'Invalid') {
        returnString +=
          _value && _value.length > 0
            ? t(`settings.${props.listType}.form.${props.valueType}Warning${key}`) + '\n'
            : '';
      }
    });

    // If we have *something* in the validation message for specific invalid types
    // add generic error message at the end

    // If we only have generic validation warnings, we wont have put anything into the return string yet
    // In this case, trigger this code path to display only a generic validation warning

    if (
      returnString.length > 0 ||
      (returnString.length === 0 &&
        props.invalidItemValues.size === 1 &&
        props.invalidItemValues.get('Invalid').length > 0)
    ) {
      returnString +=
        t(`settings.${props.listType}.form.${props.valueType}WarningInvalid`, {
          invalidList: '• ' + Array.from(invalidItems).join('\n• '),
        }) + '\n';
    }

    return returnString;
  };

  const getDuplicateWarningDetails = () => {
    return props.itemValue && props.duplicateItemValues && props.duplicateItemValues.length > 0
      ? t(`settings.${props.listType}.form.${props.valueType}WarningDuplicates`, {
          duplicateList: '• ' + props.duplicateItemValues.join('\n• '),
        }) + '\n'
      : '';
  };

  const getCountWarningDetails = () => {
    return props.itemValue && props.itemValue.length > 50
      ? t(`settings.${props.listType}.form.${props.valueType}WarningCount`)
      : '';
  };

  const hasAnyInvalidItems = () => {
    let returnValue = false;
    props.invalidItemValues.forEach((_value, key) => {
      if (returnValue === true) return;

      _value.forEach((value, i) => {
        // If there's any invalid item of any kind, return true
        returnValue = true;
        return;
      });
    });

    return returnValue;
  };

  const warningMessages = () => {
    return `${getInvalidWarningDetails()}${getDuplicateWarningDetails()}${getCountWarningDetails()}`;
  };

  const isButtonDisabled = () => {
    return (
      !props.itemName ||
      !props.itemValue ||
      (props.itemValue && props.itemValue.length <= 0) ||
      props.itemValue.length > 50
    );
  };

  const getTooltipMessage = () => {
    const valueTypeLabel = t(`settings.${props.listType}.form.${props.valueType}NameLabel`);
    const valueLabel = t(`settings.${props.listType}.form.${props.valueType}Label`);
    return t('settings.blocklistAllowlist.tooltipDisabledMessage', {
      listType: props.listType,
      valueTypeNameLabel: valueTypeLabel,
      valueLabel: valueLabel,
    });
  };

  return (
    <>
      <FormGroup variation="inline" childrenSpanWidth={true}>
        <Grid>
          <Input
            value={props.itemName}
            type="text"
            onChange={value => props.itemNameChangeHandler(value)}
            label={t(`settings.${props.listType}.form.${props.valueType}NameLabel`)}
            validationState={props.itemName ? 'complete' : 'warning'}
            validationMessage={!props.itemName && t(`settings.${props.listType}.form.${props.valueType}NameWarning`)}
            ariaLabel={t(`settings.${props.listType}.form.${props.valueType}NameLabel`)}
            placeholder={t(`settings.${props.listType}.form.${props.valueType}NamePlaceholder`)}
          />
        </Grid>
        <Grid>
          <SelectMulti
            creatable={true}
            clearable={false}
            onCreateOption={value => props.addItemValueToSelectMulti(value)}
            ariaLabel={t(`settings.${props.listType}.form.${props.valueType}Label`)}
            placeholder={t(`settings.${props.listType}.form.${props.valueType}Placeholder`)}
            validationMessage={warningMessages()}
            validationState={
              props.itemValue && props.itemValue.length > 0 && props.invalidItemValues && hasAnyInvalidItems() === false
                ? 'complete'
                : 'warning'
            }
            label={t(`settings.${props.listType}.form.${props.valueType}Label`)}
            onChange={value => props.itemValueChangeHandler(value)}
            value={props.itemValue}
          />
        </Grid>
        <Grid>
          <Gap left={40} />
          <div style={{ marginTop: '28px' }}>
            {isButtonDisabled() ? (
              <Tooltip title={getTooltipMessage()} position="top">
                <Button
                  data-testid={`primary-button-${props.listType}-${props.valueType}`}
                  variation="primary"
                  onClick={() => save()}
                  disabled={isButtonDisabled()}
                  title={t(`settings.${props.listType}.form.${props.valueType}AddButtonLabel`)}
                  fullWidth={false}
                  type="submit"
                />
              </Tooltip>
            ) : (
              <Button
                data-testid={`primary-button-${props.listType}-${props.valueType}`}
                variation="primary"
                onClick={() => save()}
                disabled={isButtonDisabled()}
                title={t(`settings.${props.listType}.form.${props.valueType}AddButtonLabel`)}
                fullWidth={false}
                type="submit"
              />
            )}
          </div>
        </Grid>
      </FormGroup>

      <BlocklistAllowlistCommentModal
        siteId={siteId}
        listType={props.listType}
        valueType={props.valueType}
        itemName={props.itemName}
        itemValue={props.itemValue}
        setInvalidItemValues={props.setInvalidItemValues}
        setDuplicateItemValues={props.setDuplicateItemValues}
        commentModalOpen={isModalOpen}
        setCommentModalOpen={setIsModalOpen}
        addBlocklistAllowlistCallback={props.addBlocklistAllowlistCallback}
        resetItem={resetItem}
      ></BlocklistAllowlistCommentModal>
    </>
  );
};

export default BlocklistAllowlistForm;
